import { useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import type { Question, QuestionsByType, QuestionType, IntensityLevel, GenderType } from '../types';

const CSV_URL = 'https://hpify-config.b-cdn.net/trueordaredb.csv';

// Default questions as fallback
const defaultQuestions: Question[] = [
  {
    id: '1',
    type: 'truth',
    intensity: 'mild',
    content: "What's the most embarrassing song on your playlist?",
    gender: 'neutral'
  },
  {
    id: '2',
    type: 'truth',
    intensity: 'mild',
    content: "What's your ideal type of partner?",
    gender: 'neutral'
  },
  {
    id: '3',
    type: 'truth',
    intensity: 'moderate',
    content: "What's your biggest fantasy that you haven't fulfilled yet?",
    gender: 'neutral'
  },
  {
    id: '4',
    type: 'truth',
    intensity: 'spicy',
    content: "What's the wildest place you've ever wanted to get intimate?",
    gender: 'neutral'
  },
  {
    id: '5',
    type: 'dare',
    intensity: 'mild',
    content: "Give your partner a 30-second neck massage.",
    gender: 'neutral'
  },
  {
    id: '6',
    type: 'dare',
    intensity: 'mild',
    content: "Whisper something seductive in your partner's ear.",
    gender: 'neutral'
  },
  {
    id: '7',
    type: 'dare',
    intensity: 'moderate',
    content: "Kiss your partner in a place of their choosing.",
    gender: 'neutral'
  },
  {
    id: '8',
    type: 'dare',
    intensity: 'spicy',
    content: "Perform a seductive dance for 1 minute.",
    gender: 'neutral'
  }
];

// CSV row structure (based on actual CSV format)
interface CSVRow {
  id: string;
  type: string;
  intensity: string;
  content: string;
  gender: string;
}

// Validate and parse question type
function parseType(typeValue: string): QuestionType {
  const normalized = typeValue.trim().toLowerCase();
  if (normalized === 'truth') return 'truth';
  if (normalized === 'dare') return 'dare';
  return 'truth'; // default fallback
}

// Validate and parse intensity level
function parseIntensity(intensityValue: string): IntensityLevel {
  const normalized = intensityValue.trim().toLowerCase();
  if (normalized === 'mild') return 'mild';
  if (normalized === 'moderate') return 'moderate';
  if (normalized === 'spicy') return 'spicy';
  if (normalized === 'extreme') return 'extreme';
  return 'mild'; // default fallback
}

// Validate and parse gender
function parseGender(genderValue: string): GenderType | 'neutral' {
  const normalized = genderValue.trim().toLowerCase();
  if (normalized === 'male') return 'male';
  if (normalized === 'female') return 'female';
  return 'neutral'; // default for any other value
}

// Convert CSV row to Question
function csvRowToQuestion(row: CSVRow): Question | null {
  // Skip empty rows
  if (!row.content || !row.type) {
    return null;
  }

  return {
    id: row.id || `csv-${Math.random().toString(36).substr(2, 9)}`,
    content: row.content.trim(),
    type: parseType(row.type),
    intensity: parseIntensity(row.intensity),
    gender: parseGender(row.gender)
  };
}

// Organize questions by type and intensity
function organizeQuestions(questions: Question[]): QuestionsByType {
  const organized: QuestionsByType = {
    truth: { mild: [], moderate: [], spicy: [], extreme: [] },
    dare: { mild: [], moderate: [], spicy: [], extreme: [] }
  };

  questions.forEach(q => {
    if (organized[q.type] && organized[q.type][q.intensity]) {
      organized[q.type][q.intensity].push(q);
    }
  });

  return organized;
}

export function useQuestions() {
  const [questions, setQuestions] = useState<QuestionsByType>({
    truth: { mild: [], moderate: [], spicy: [], extreme: [] },
    dare: { mild: [], moderate: [], spicy: [], extreme: [] }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(CSV_URL);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const csvText = await response.text();
      
      Papa.parse<CSVRow>(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim().toLowerCase(),
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            const parsedQuestions = results.data
              .map(row => csvRowToQuestion(row))
              .filter((q): q is Question => q !== null);
            
            if (parsedQuestions.length > 0) {
              setQuestions(organizeQuestions(parsedQuestions));
            } else {
              console.warn('No valid questions parsed from CSV, using defaults');
              setQuestions(organizeQuestions(defaultQuestions));
            }
          } else {
            console.warn('Empty CSV data, using defaults');
            setQuestions(organizeQuestions(defaultQuestions));
          }
          setIsLoading(false);
        },
        error: (parseError: Error) => {
          console.error('CSV parse error:', parseError);
          setError('Failed to parse questions data');
          setQuestions(organizeQuestions(defaultQuestions));
          setIsLoading(false);
        }
      });
    } catch (err) {
      console.error('Error loading questions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setQuestions(organizeQuestions(defaultQuestions));
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const getRandomQuestion = useCallback((
    type: QuestionType,
    intensity: IntensityLevel,
    playerGender: GenderType
  ): string => {
    const questionList = questions[type][intensity];

    if (questionList.length === 0) {
      return `No ${type} questions available for ${intensity} intensity.`;
    }

    // Filter questions by matching gender (including neutral)
    const eligibleQuestions = questionList.filter(q =>
      q.gender === playerGender || q.gender === 'neutral'
    );

    if (eligibleQuestions.length === 0) {
      // Fallback to neutral questions if no gender-specific ones
      const neutralQuestions = questionList.filter(q => q.gender === 'neutral');
      if (neutralQuestions.length === 0) {
        return "No matching questions found!";
      }
      const randomIndex = Math.floor(Math.random() * neutralQuestions.length);
      return neutralQuestions[randomIndex].content;
    }

    const randomIndex = Math.floor(Math.random() * eligibleQuestions.length);
    return eligibleQuestions[randomIndex].content;
  }, [questions]);

  return {
    questions,
    isLoading,
    error,
    loadQuestions,
    getRandomQuestion
  };
}
